/**
 * Workflow Serializer Service
 *
 * Handles serialization and deserialization of workflow definitions
 * with proper handling of:
 * - Agent action inputs with modelConfiguration
 * - Tool definitions with agentParameterSchema
 * - Value segments to/from expression strings
 * - Connection references
 */

import type {
  WorkflowDefinition,
  Action,
  Trigger,
  AgentAction,
  AgentTool,
} from '@/types/workflowDefinition';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import type { ValueSegment } from '@/types/valueSegment';
import { serializeSegmentsToValue } from './tokenParsingService';
import { telemetryService } from './telemetryService';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Serialization options for workflow export
 */
export interface SerializationOptions {
  /** Whether to include empty objects */
  includeEmptyObjects?: boolean | undefined;
  /** Whether to format the output */
  pretty?: boolean | undefined;
  /** Encoding options for string values */
  encoding?: EncodingOptions | undefined;
  /** Casting options for type conversion */
  casting?: CastingOptions | undefined;
}

/**
 * Encoding options for parameter values
 * Based on LogicAppsUX serialization patterns
 */
export interface EncodingOptions {
  /** Base64 encode binary values */
  base64Binary?: boolean | undefined;
  /** HTML encode string values for web safety */
  htmlEncode?: boolean | undefined;
  /** URL encode string values for query parameters */
  urlEncode?: boolean | undefined;
  /** JSON encode object/array values as strings */
  jsonEncode?: boolean | undefined;
}

/**
 * Type casting options for parameter values
 * Based on LogicAppsUX type casting patterns
 */
export interface CastingOptions {
  /** Cast strings to numbers when applicable */
  stringToNumber?: boolean | undefined;
  /** Cast strings to booleans when applicable */
  stringToBoolean?: boolean | undefined;
  /** Cast arrays to CSV strings */
  arrayToCsv?: boolean | undefined;
  /** Preserve null values (vs converting to empty strings) */
  preserveNulls?: boolean | undefined;
}

/**
 * Result of workflow validation
 */
export interface ValidationResult {
  /** Whether the workflow is valid */
  isValid: boolean;
  /** Validation errors */
  errors: ValidationError[];
  /** Validation warnings */
  warnings: ValidationWarning[];
}

export interface ValidationError {
  /** Error code */
  code: string;
  /** Error message */
  message: string;
  /** Path to the error location */
  path: string;
  /** Severity */
  severity: 'error';
}

export interface ValidationWarning {
  /** Warning code */
  code: string;
  /** Warning message */
  message: string;
  /** Path to the warning location */
  path: string;
  /** Severity */
  severity: 'warning';
}

// Default System message injected for non-V2 agents when the user hasn't
// provided one. Backend rejects User-only message arrays for these agent
// types (see EdgeFlowDefinitionValidationEngine).
export const DEFAULT_AGENT_SYSTEM_MESSAGE = 'You are a helpful assistant.';

/**
 * Agent inputs structure from workflow JSON
 */
export interface AgentInputs {
  systemPrompt?: string | undefined;
  modelConfiguration?: ModelConfiguration | undefined;
  maxIterations?: number | undefined;
  temperature?: number | undefined;
  maxTokens?: number | undefined;
  topP?: number | undefined;
  [key: string]: unknown;
}

/**
 * Model configuration for Agent actions
 */
export interface ModelConfiguration {
  deploymentName?: string | undefined;
  modelName?: string | undefined;
  apiVersion?: string | undefined;
  provider?: string | undefined;
}

/**
 * Parameter value that can be either raw or value segments
 */
export type ParameterValue = string | number | boolean | object | ValueSegment[];

// =============================================================================
// SERIALIZATION FUNCTIONS
// =============================================================================

/**
 * Serialize a workflow definition to JSON string
 */
export function serializeWorkflow(
  workflow: WorkflowDefinition,
  options: SerializationOptions = {}
): string {
  const { pretty = true } = options;

  // Create a clean copy of the workflow
  const cleanedWorkflow = cleanWorkflowForSerialization(workflow, options);

  return pretty
    ? JSON.stringify(cleanedWorkflow, null, 2)
    : JSON.stringify(cleanedWorkflow);
}

/**
 * Clean workflow for serialization by removing internal-only properties.
 * Exported so that callers who need the cleaned object (not the string)
 * can use it directly — e.g., the publish path.
 */
export function cleanWorkflowForSerialization(
  workflow: WorkflowDefinition,
  options: SerializationOptions
): WorkflowDefinition {
  const { includeEmptyObjects = false } = options;

  const cleanedDefinition = {
    ...workflow.definition,
    triggers: cleanTriggers(workflow.definition.triggers),
    actions: cleanActions(workflow.definition.actions, includeEmptyObjects),
  };

  // Workflow-level layoutMode (auto/manual) is a client-only canvas concern;
  // its persistence lives in the `nodesMetadata.json` sidecar, not the
  // workflow definition itself. Lives at `workflow.metadata.layoutMode`
  // (the top-level metadata bag, not the definition's).
  let cleanedWorkflowMetadata: Record<string, unknown> | undefined = workflow.metadata as
    | Record<string, unknown>
    | undefined;
  if (cleanedWorkflowMetadata) {
    cleanedWorkflowMetadata = { ...cleanedWorkflowMetadata };
    delete cleanedWorkflowMetadata.layoutMode;
    if (Object.keys(cleanedWorkflowMetadata).length === 0 && !includeEmptyObjects) {
      cleanedWorkflowMetadata = undefined;
    }
  }

  // Remove empty outputs/parameters if they exist
  if (!includeEmptyObjects) {
    if (cleanedDefinition.outputs && Object.keys(cleanedDefinition.outputs).length === 0) {
      delete cleanedDefinition.outputs;
    }
    if (cleanedDefinition.parameters && Object.keys(cleanedDefinition.parameters).length === 0) {
      delete cleanedDefinition.parameters;
    }
  }

  const result: WorkflowDefinition = {
    ...workflow,
    definition: cleanedDefinition,
  };
  if (cleanedWorkflowMetadata !== undefined) {
    (result as { metadata?: Record<string, unknown> }).metadata = cleanedWorkflowMetadata;
  } else {
    delete (result as { metadata?: unknown }).metadata;
  }
  return result;
}

/**
 * Return the object if it has at least one own key, else `undefined`. Used
 * by every draft-save / publish path to drop empty configs from the API
 * payload (the runtime treats `{}` and absent fields differently).
 */
export function nonEmptyObject<T extends object>(obj: T | null | undefined): T | undefined {
  return obj && Object.keys(obj).length > 0 ? obj : undefined;
}

/**
 * API-ready payload for `saveDraftWorkflow`, with empty configs normalized
 * to `undefined` so callers don't need to repeat the `Object.keys(...).length`
 * check at every save site.
 */
export interface DraftSavePayload {
  workflowDefinition: { definition: Record<string, unknown>; kind: string };
  connectionsConfig?: ConnectionsConfiguration;
  parametersConfig?: Record<string, unknown>;
  notesConfig?: Record<string, unknown>;
}

/**
 * Build the payload bag that `saveDraftWorkflow` accepts from a live
 * workflow snapshot + sibling configs. Centralizes the cleaning, `kind`
 * fallback, and empty-object normalization that every draft save path
 * (autosave, manual save, save-and-switch) needs to perform.
 */
export function buildDraftSavePayload(
  workflow: WorkflowDefinition,
  connectionsConfig: ConnectionsConfiguration | null | undefined,
  parametersConfig?: Record<string, unknown> | null | undefined,
): DraftSavePayload {
  const cleaned = cleanWorkflowForSerialization(workflow, {});
  // Strip placeholder connections (empty connection.id) that the runtime
  // cannot parse — they exist in-memory so the designer can render connector
  // icons, but must not be persisted to connections-draft.json.
  const connections = nonEmptyObject(stripPlaceholderConnections(connectionsConfig));
  const parameters = nonEmptyObject(parametersConfig);
  const notes = nonEmptyObject(cleaned.notes as Record<string, unknown> | undefined);
  return {
    workflowDefinition: {
      definition: cleaned.definition,
      kind: cleaned.kind || workflow.kind || 'Stateful',
    },
    ...(connections && { connectionsConfig: connections }),
    ...(parameters && { parametersConfig: parameters }),
    ...(notes && { notesConfig: notes }),
  };
}

/**
 * Remove placeholder managed API connections that have an empty connection ID.
 * These are created by template apply for unresolved connections and must not
 * be sent to the runtime (which cannot deserialize them).
 */
function stripPlaceholderConnections(
  config: ConnectionsConfiguration | null | undefined,
): ConnectionsConfiguration | null {
  if (!config) return null;

  let managedApiConnections = config.managedApiConnections;
  if (managedApiConnections) {
    const filtered: Record<string, typeof managedApiConnections[string]> = {};
    for (const [key, conn] of Object.entries(managedApiConnections)) {
      if (conn.connection?.id?.trim()) {
        filtered[key] = conn;
      }
    }
    managedApiConnections = Object.keys(filtered).length > 0 ? filtered : undefined;
  }

  const result: ConnectionsConfiguration = {
    ...config,
    ...(managedApiConnections !== undefined ? { managedApiConnections } : {}),
  };

  // If we removed the only section, delete the empty key
  if (!managedApiConnections) {
    delete result.managedApiConnections;
  }

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Clean triggers for serialization
 */
function cleanTriggers(
  triggers: Record<string, Trigger>,
): Record<string, Trigger> {
  const cleaned: Record<string, Trigger> = {};

  for (const [name, trigger] of Object.entries(triggers)) {
    cleaned[name] = cleanOperation(trigger, false);
  }

  return cleaned;
}

/**
 * Clean actions for serialization
 */
function cleanActions(
  actions: Record<string, Action>,
  includeEmptyObjects: boolean
): Record<string, Action> {
  const cleaned: Record<string, Action> = {};

  for (const [name, action] of Object.entries(actions)) {
    cleaned[name] = cleanAction(action, includeEmptyObjects);
  }

  return cleaned;
}

/**
 * Recursively clean an action for serialization. Strips client-only
 * metadata from the action itself, then descends into Agent tool inner
 * actions and scope branches (`actions`, `else.actions`, `cases[*].actions`)
 * so nested operations get the same treatment.
 *
 * Exported so display surfaces (e.g. the per-node Code tab) can render
 * exactly what the save pipeline would persist, including for scopes and
 * agents. For top-level triggers or operations known not to contain
 * nested children, `cleanOperation` is a cheaper alternative.
 */
export function cleanAction(
  action: Action,
  includeEmptyObjects: boolean
): Action {
  const cleaned = cleanOperation(action, includeEmptyObjects);

  // Handle Agent-specific cleaning
  if (isAgentAction(cleaned)) {
    return cleanAgentAction(cleaned, includeEmptyObjects);
  }

  // Handle nested actions (If, Switch, Foreach)
  if (hasNestedActions(cleaned)) {
    return cleanNestedAction(cleaned, includeEmptyObjects);
  }

  return cleaned;
}

/**
 * Internal-only properties that exist on action/trigger objects in the store
 * for UI state tracking but must never appear in serialized output.
 * These properties are not part of the Logic Apps runtime schema.
 *
 * Add new internal properties here rather than scattering strip logic
 * across the codebase — this is the single serialization boundary.
 */
const INTERNAL_OPERATION_PROPERTIES: ReadonlySet<string> = new Set([
  'detached', // Marks free-floating actions in the graph; consumed by useWorkflowGraph/useTokenSources
]);

const INTERNAL_METADATA_PROPERTIES: ReadonlySet<string> = new Set([
  'position',
  'toolNodePositions',
  'swaggerPathTemplate',
  'detached',
]);

/**
 * Strip client-only metadata and internal-only properties from a SINGLE
 * operation. Does NOT descend into nested action containers (scope branches,
 * Agent tool actions) — use {@link cleanAction} for that.
 *
 * Exported so callers that know they're handling a leaf operation (e.g. a
 * trigger) can skip the recursive dispatch.
 */
export function cleanOperation(
  operation: Action,
  includeEmptyObjects: boolean,
): Action;
export function cleanOperation(
  operation: Trigger,
  includeEmptyObjects: boolean,
): Trigger;
export function cleanOperation(
  operation: Action | Trigger,
  includeEmptyObjects: boolean
): Action | Trigger {
  const cleaned = {
    ...(operation as Record<string, unknown>),
  } as Action | Trigger;
  const cleanedRecord = cleaned as Record<string, unknown>;

  // Handle metadata
  const metadata = cleanedRecord.metadata;
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    const cleanedMetadata = {
      ...(metadata as Record<string, unknown>),
    };

    // Client-only fields. Pixel positions and tool node positions live in the
    // `workflowLayoutStore`; `swaggerPathTemplate` and `detached` persist via
    // the `nodesMetadata.json` sidecar. None of these belong in workflow.json.
    delete (cleanedMetadata as { position?: unknown }).position;
    delete (cleanedMetadata as { toolNodePositions?: unknown }).toolNodePositions;
    delete (cleanedMetadata as { swaggerPathTemplate?: unknown }).swaggerPathTemplate;
    delete (cleanedMetadata as { detached?: unknown }).detached;

    // Remove empty metadata
    if (Object.keys(cleanedMetadata).length === 0 && !includeEmptyObjects) {
      delete cleanedRecord.metadata;
    } else {
      cleanedRecord.metadata = cleanedMetadata;
    }
  }

  // Strip all internal-only UI properties that shouldn't reach the runtime.
  // LogicApps handles this by building definitions from scratch in the serializer;
  // Otto modifies action objects in-place, so we strip here at the serialization boundary.
  for (const prop of INTERNAL_OPERATION_PROPERTIES) {
    if (prop in cleaned) {
      delete (cleaned as Record<string, unknown>)[prop];
    }
  }

  // Clean empty runAfter
  const runAfter = cleanedRecord.runAfter;
  if (
    runAfter &&
    typeof runAfter === 'object' &&
    !Array.isArray(runAfter) &&
    Object.keys(runAfter).length === 0 &&
    !includeEmptyObjects
  ) {
    delete cleanedRecord.runAfter;
  }

  return cleaned;
}

/**
 * Restore client-only operation state after a user edits the cleaned runtime
 * JSON shown in the node code editor. Runtime fields come exclusively from the
 * edited object so deletions are honored; only properties stripped by the
 * serializer are copied from the live store object.
 */
export function preserveInternalOperationState(
  editedOperation: Action,
  currentOperation: Action,
): Action;
export function preserveInternalOperationState(
  editedOperation: Trigger,
  currentOperation: Trigger,
): Trigger;
export function preserveInternalOperationState(
  editedOperation: Action | Trigger,
  currentOperation: Action | Trigger,
): Action | Trigger {
  const restored = structuredClone(editedOperation) as Action | Trigger;
  const restoredRecord = restored as Record<string, unknown>;
  const currentRecord = currentOperation as Record<string, unknown>;

  for (const property of INTERNAL_OPERATION_PROPERTIES) {
    if (Object.hasOwn(currentRecord, property)) {
      restoredRecord[property] = structuredClone(currentRecord[property]);
    }
  }

  const currentMetadata = currentRecord.metadata as Record<string, unknown> | undefined;
  if (currentMetadata) {
    const restoredMetadata = {
      ...((restoredRecord.metadata as Record<string, unknown> | undefined) ?? {}),
    };
    for (const property of INTERNAL_METADATA_PROPERTIES) {
      if (Object.hasOwn(currentMetadata, property)) {
        restoredMetadata[property] = structuredClone(currentMetadata[property]);
      }
    }
    if (Object.keys(restoredMetadata).length > 0) {
      restoredRecord.metadata = restoredMetadata;
    }
  }

  preserveNestedActionState(restoredRecord, currentRecord);
  return restored;
}

function preserveActionMapState(
  editedActions: unknown,
  currentActions: unknown,
): void {
  if (
    !editedActions ||
    typeof editedActions !== 'object' ||
    !currentActions ||
    typeof currentActions !== 'object'
  ) {
    return;
  }

  const editedMap = editedActions as Record<string, Action>;
  const currentMap = currentActions as Record<string, Action>;
  for (const [name, editedAction] of Object.entries(editedMap)) {
    const currentAction = currentMap[name];
    if (currentAction) {
      editedMap[name] = preserveInternalOperationState(editedAction, currentAction);
    }
  }
}

function preserveNestedActionState(
  edited: Record<string, unknown>,
  current: Record<string, unknown>,
): void {
  preserveActionMapState(edited.actions, current.actions);

  const editedElse = edited.else as Record<string, unknown> | undefined;
  const currentElse = current.else as Record<string, unknown> | undefined;
  preserveActionMapState(editedElse?.actions, currentElse?.actions);

  const editedDefault = edited.default as Record<string, unknown> | undefined;
  const currentDefault = current.default as Record<string, unknown> | undefined;
  preserveActionMapState(editedDefault?.actions, currentDefault?.actions);

  const editedCases = edited.cases as Record<string, Record<string, unknown>> | undefined;
  const currentCases = current.cases as Record<string, Record<string, unknown>> | undefined;
  if (editedCases && currentCases) {
    for (const [caseName, editedCase] of Object.entries(editedCases)) {
      preserveActionMapState(editedCase.actions, currentCases[caseName]?.actions);
    }
  }

  const editedTools = edited.tools as
    | Record<string, { actions?: Record<string, Action> }>
    | undefined;
  const currentTools = current.tools as
    | Record<string, { actions?: Record<string, Action> }>
    | undefined;
  if (editedTools && currentTools) {
    for (const [toolName, editedTool] of Object.entries(editedTools)) {
      preserveActionMapState(
        editedTool.actions,
        currentTools[toolName]?.actions,
      );
    }
  }
}

/**
 * Clean Agent action for serialization
 */
function cleanAgentAction(
  action: AgentAction,
  includeEmptyObjects: boolean
): AgentAction {
  const cleaned: AgentAction = { ...action };

  // Ensure limit is always present — backend requires it on Agent actions
  if (!cleaned.limit) {
    cleaned.limit = { count: 60, timeout: 'PT1H' };
  }

  // Clean tools
  if (cleaned.tools) {
    const cleanedTools: Record<string, AgentTool> = {};

    for (const [toolName, tool] of Object.entries(cleaned.tools)) {
      cleanedTools[toolName] = cleanAgentTool(tool, includeEmptyObjects);
    }

    cleaned.tools = cleanedTools;
  }

  // Ensure modelConfiguration is properly structured
  // Note: We must copy nested objects before mutating them since store state may be frozen
  if (cleaned.inputs && typeof cleaned.inputs === 'object' && !Array.isArray(cleaned.inputs)) {
    const inputs = { ...cleaned.inputs } as AgentInputs;
    cleaned.inputs = inputs;

    // Ensure parameters.deploymentId is present — backend requires the key on
    // Agent actions, except hosted agents which don't use deploymentId.
    const params = (inputs.parameters && typeof inputs.parameters === 'object')
      ? { ...inputs.parameters } as Record<string, unknown>
      : {} as Record<string, unknown>;

    // Resolve the agent model type up front — it drives both the GitHub Copilot
    // normalization here and the messages contract below.
    let agentModelType = typeof params.agentModelType === 'string'
      ? params.agentModelType
      : (inputs.agentModelType as string | undefined);

    // GitHub Copilot normalization. The UI uses the `GitHubCopilot` model type so
    // the manifest surfaces the GHCP model picker, which writes the chosen model
    // into `deploymentId`. The runtime only accepts the `V1ChatCompletionsService`
    // shape, where the model lives in `modelId`. Rewrite the type and migrate the
    // model value at this single serialization boundary so the live UI stays intact
    // (provider matching, model picker, coding-variant detection — which keys off
    // agentHarness.type, not the model type) while the published payload is valid.
    // The connection type stays GitHubCopilot, so token brokering is unaffected.
    if (agentModelType === 'GitHubCopilot') {
      agentModelType = 'V1ChatCompletionsService';
      params.agentModelType = 'V1ChatCompletionsService';
      if (inputs.agentModelType === 'GitHubCopilot') {
        (inputs as Record<string, unknown>).agentModelType = 'V1ChatCompletionsService';
      }
      const ghcpModel = typeof params.deploymentId === 'string' ? params.deploymentId : '';
      if (ghcpModel && !params.modelId) {
        params.modelId = ghcpModel;
      }
      // GHCP uses modelId, not deploymentId — drop the deploymentId key so we
      // don't emit the empty default below for this model type.
      delete params.deploymentId;
    } else if (!params.deploymentId && agentModelType !== 'V1ChatCompletionsService' && agentModelType !== 'FoundryHostedAgent') {
      // Ensure parameters.deploymentId is present for agent model types that
      // use it (AzureOpenAI, Foundry, APIM, etc.). V1ChatCompletionsService
      // uses modelId instead, and hosted agents don't use deploymentId, so
      // skip both. Leave empty so the user picks one in DeploymentField;
      // backend validation will flag the empty value for model types that
      // require it.
      params.deploymentId = '';
    }

    // Normalize the messages array before save. The backend validator
    // (EdgeFlowDefinitionValidationEngine) enforces only a non-empty messages array, per-kind role
    // constraints, and non-empty content (empty content is REJECTED) — it does NOT require a System
    // entry. The rules below are this serializer's normalization POLICY:
    //  - FoundryAgentServiceV2: keep User-only entries.
    //  - FoundryHostedAgent: optional author-provided System message + a single User entry.
    //  - all other agent model types: ensure at least one System entry, injecting a default as a
    //    product/runtime baseline so the agent always has instructions (not to satisfy validation).
    // When a required message is left blank we still emit an empty User entry on purpose, so the
    // backend returns the actionable missing-content error rather than a missing-property one.
    const isV2 = agentModelType === 'FoundryAgentServiceV2';
    const isHostedAgent = agentModelType === 'FoundryHostedAgent';
    const existing = Array.isArray(params.messages)
      ? (params.messages as Array<{ role?: unknown; content?: unknown }>)
      : [];
    const hasRole = (msg: { role?: unknown }, role: string): boolean =>
      typeof msg?.role === 'string' && msg.role.toLowerCase() === role;
    const hasNonEmptyContent = (msg: { content?: unknown }): boolean =>
      typeof msg?.content === 'string' && msg.content.trim().length > 0;

    if (isV2) {
      // FoundryAgentServiceV2 only accepts User-role entries. Drop anything
      // else (e.g. a System message left over from a non-V2 → V2 model switch)
      // and re-seed with an empty User entry if nothing valid remains.
      const userOnly = existing.filter((m) => hasRole(m, 'user'));
      params.messages = userOnly.length > 0 ? userOnly : [{ role: 'User', content: '' }];
    } else if (isHostedAgent) {
      // FoundryHostedAgent carries an OPTIONAL author-provided System message (forwarded to the
      // shim's system_prompt at runtime) and an OPTIONAL User input message. Emit each ONLY when it
      // has non-empty content, producing [System?, User?] (System first for instructions, User last
      // for the prompt). This mirrors parameterUtils and matches normal-agent parity: an
      // instruction-driven hosted agent (rich System instructions + tools/skills, no user prompt)
      // serializes to a valid System-only [System] — the runtime allows System-only for these
      // workflows and rejects empty content, so we must NOT emit a blank User entry alongside a real
      // System message. We deliberately do NOT inject DEFAULT_AGENT_SYSTEM_MESSAGE (absent System →
      // the shim applies its own default), and we drop stray Assistant/Tool entries. Only when
      // NEITHER a System nor a User message has content do we emit a single empty User entry, so the
      // backend returns the actionable missing-content error rather than a missing-property one.
      const systemMsg = existing.find((m) => hasRole(m, 'system') && hasNonEmptyContent(m));
      const lastUserMsg = [...existing].reverse().find((m) => hasRole(m, 'user') && hasNonEmptyContent(m));
      const normalized: Array<{ role: string; content: unknown }> = [];
      if (systemMsg) normalized.push({ role: 'System', content: systemMsg.content });
      if (lastUserMsg) normalized.push({ role: 'User', content: lastUserMsg.content });
      params.messages = normalized.length > 0 ? normalized : [{ role: 'User', content: '' }];
    } else if (existing.length === 0) {
      params.messages = [{ role: 'System', content: DEFAULT_AGENT_SYSTEM_MESSAGE }];
    } else {
      const hasSystem = existing.some(
        (m) => hasRole(m, 'system') && typeof m.content === 'string' && m.content.trim().length > 0
      );
      params.messages = hasSystem
        ? existing
        : [{ role: 'System', content: DEFAULT_AGENT_SYSTEM_MESSAGE }, ...existing];
    }
    inputs.parameters = params;

    if (inputs.modelConfiguration) {
      // Ensure all fields are present even if undefined
      inputs.modelConfiguration = {
        deploymentName: inputs.modelConfiguration.deploymentName,
        modelName: inputs.modelConfiguration.modelName,
        apiVersion: inputs.modelConfiguration.apiVersion,
        provider: inputs.modelConfiguration.provider,
      };

      // Remove undefined fields
      for (const key of Object.keys(inputs.modelConfiguration)) {
        if (inputs.modelConfiguration[key as keyof ModelConfiguration] === undefined) {
          delete inputs.modelConfiguration[key as keyof ModelConfiguration];
        }
      }
    }
  }

  return cleaned;
}

/**
 * Clean Agent tool for serialization
 */
function cleanAgentTool(
  tool: AgentTool,
  includeEmptyObjects: boolean
): AgentTool {
  const cleaned: AgentTool = { ...tool };

  // McpClientTool never has an `actions` branch — it carries a connection
  // reference + parameters and the runtime resolves the tool list dynamically.
  // Strip any vestigial `actions` (including empty `{}`) so the serialized shape
  // matches the LA Standard runtime contract and stays clean even if some other
  // code path accidentally injected one.
  if ((cleaned as { type?: string }).type === 'McpClientTool') {
    delete (cleaned as { actions?: unknown }).actions;
  } else if (cleaned.actions) {
    // Clean nested actions within the tool
    cleaned.actions = cleanActions(cleaned.actions, includeEmptyObjects);
  }

  // Keep agentParameterSchema as-is
  return cleaned;
}

/**
 * Clean nested action (If, Switch, Foreach) for serialization
 */
function cleanNestedAction(
  action: Action,
  includeEmptyObjects: boolean
): Action {
  const cleaned = { ...action };
  const typedAction = cleaned as { actions?: Record<string, Action>; else?: { actions?: Record<string, Action> }; cases?: Record<string, { actions?: Record<string, Action> }> };

  // Clean actions branch
  if (typedAction.actions) {
    typedAction.actions = cleanActions(typedAction.actions, includeEmptyObjects);
  }

  // Foreach actions do not allow an 'inputs' property — remove it
  if (cleaned.type === 'Foreach') {
    delete (cleaned as Record<string, unknown>).inputs;
  }

  // Clean else branch (for If actions)
  // Must shallow-copy `else` before mutating since it may be a frozen store reference
  if (typedAction.else?.actions) {
    typedAction.else = { ...typedAction.else, actions: cleanActions(typedAction.else.actions, includeEmptyObjects) };
  }

  // Clean cases (for Switch actions)
  // Must shallow-copy each case object before mutating since they may be frozen store references
  if (typedAction.cases) {
    const cleanedCases: typeof typedAction.cases = {};
    for (const caseName of Object.keys(typedAction.cases)) {
      const caseObj = typedAction.cases[caseName];
      if (caseObj) {
        cleanedCases[caseName] = caseObj.actions
          ? { ...caseObj, actions: cleanActions(caseObj.actions, includeEmptyObjects) }
          : caseObj;
      }
    }
    typedAction.cases = cleanedCases;
  }

  // Clean default branch (for Switch actions). Must be normalized like every other branch —
  // otherwise an agent in a Switch `default` never runs cleanAgentAction, so its messages skip
  // the hosted/V2 normalization (e.g. a hosted agent's blank User entry isn't dropped) and reach
  // the runtime unnormalized, failing with the cryptic "missing content" error even though the
  // same agent publishes fine in an `actions`/`else`/`cases[*]` branch. Shallow-copy before
  // mutating since `default` may be a frozen store reference.
  const typedDefault = cleaned as { default?: { actions?: Record<string, Action> } };
  if (typedDefault.default?.actions) {
    typedDefault.default = {
      ...typedDefault.default,
      actions: cleanActions(typedDefault.default.actions, includeEmptyObjects),
    };
  }

  return cleaned;
}

// =============================================================================
// PARAMETER VALUE SERIALIZATION
// =============================================================================

/**
 * Swagger type compatibility map. Mirrors LogicAppsUX SWAGGER.TOKENS table —
 * lists which value types are accepted for a given parameter type. Used to
 * decide whether a string editor value should be coerced to a richer type
 * (object, array, number, boolean) when serializing to the workflow definition.
 */
const TYPE_COERCION_TARGETS: Record<string, ReadonlySet<string>> = {
  any: new Set(['any', 'array', 'boolean', 'file', 'integer', 'number', 'object', 'string']),
  array: new Set(['any', 'array']),
  boolean: new Set(['any', 'boolean']),
  file: new Set(['any', 'file', 'boolean', 'integer', 'number', 'object', 'string']),
  integer: new Set(['any', 'integer', 'number']),
  number: new Set(['any', 'integer', 'number']),
  object: new Set(['any', 'array', 'boolean', 'file', 'integer', 'number', 'object', 'string']),
  string: new Set(['any', 'boolean', 'integer', 'number', 'string']),
};

/**
 * Try to parse a string as JSON if it looks like an object or array.
 * Returns the parsed value if successful, or the original string if not.
 *
 * Pattern from LogicAppsUX: only attempt parsing if string starts with { or [
 */
function tryParseJsonObjectOrArray(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

/**
 * Coerce a string editor value to the parameter's declared type.
 * Only coerces when the resulting native type is allowed by TYPE_COERCION_TARGETS
 * for the given parameterType. Falls back to the original string when coercion
 * is not allowed or not possible.
 */
function coerceStringToType(value: string, parameterType: string): unknown {
  const allowed = TYPE_COERCION_TARGETS[parameterType];

  // Unknown type → preserve legacy heuristic (object/array parse only)
  if (!allowed) {
    return tryParseJsonObjectOrArray(value);
  }

  // string param → never re-interpret JSON-looking input as a richer type
  if (parameterType === 'string') {
    return value;
  }

  const trimmed = value.trim();

  // boolean coercion
  if (allowed.has('boolean')) {
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
  }

  // numeric coercion (only when the entire string is a finite number)
  if ((allowed.has('integer') || allowed.has('number')) && trimmed.length > 0) {
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber) && /^-?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(trimmed)) {
      if (allowed.has('integer') && Number.isInteger(asNumber)) {
        return asNumber;
      }
      if (allowed.has('number')) {
        return asNumber;
      }
    }
  }

  // object/array coercion via JSON parse
  if (allowed.has('object') || allowed.has('array')) {
    const parsed = tryParseJsonObjectOrArray(value);
    if (parsed !== value) {
      const isArr = Array.isArray(parsed);
      const isObj = !isArr && typeof parsed === 'object' && parsed !== null;
      if ((isArr && allowed.has('array')) || (isObj && allowed.has('object'))) {
        return parsed;
      }
    }
  }

  return value;
}

/**
 * Serialize a parameter value to its workflow representation.
 * Handles both raw values and value segments.
 *
 * @param value The editor value to serialize
 * @param parameterType Optional swagger/manifest type (e.g. 'any', 'string', 'object').
 *                      When provided, coercion is constrained by the SWAGGER TOKENS
 *                      compatibility map. When omitted, falls back to the legacy
 *                      heuristic (parse strings starting with `{` or `[`).
 */
export function serializeParameterValue(value: ParameterValue, parameterType?: string): unknown {
  // If it's an array of value segments, convert to string
  if (Array.isArray(value) && isValueSegmentArray(value)) {
    return serializeSegmentsToValue(value);
  }

  // String values: type-aware coercion when type is known, otherwise legacy heuristic
  if (typeof value === 'string') {
    return parameterType
      ? coerceStringToType(value, parameterType)
      : tryParseJsonObjectOrArray(value);
  }

  // Return raw value as-is
  return value;
}

/**
 * Serialize multiple parameter values
 */
export function serializeParameterValues(
  values: Record<string, ParameterValue>
): Record<string, unknown> {
  const serialized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    serialized[key] = serializeParameterValue(value);
  }

  return serialized;
}

/**
 * Prepare Agent inputs for serialization
 */
export function serializeAgentInputs(
  inputs: Record<string, ParameterValue>
): AgentInputs {
  const serialized: AgentInputs = {};

  for (const [key, value] of Object.entries(inputs)) {
    if (key === 'modelConfiguration' && typeof value === 'object' && !Array.isArray(value)) {
      // Keep modelConfiguration as object
      serialized.modelConfiguration = value as ModelConfiguration;
    } else {
      // Serialize other values
      serialized[key] = serializeParameterValue(value);
    }
  }

  return serialized;
}

// =============================================================================
// VALIDATION
// =============================================================================

/**
 * Validate a workflow definition before saving
 */
export function validateWorkflow(workflow: WorkflowDefinition): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate basic structure
  if (!workflow.definition) {
    errors.push({
      code: 'MISSING_DEFINITION',
      message: 'Workflow definition is missing',
      path: '',
      severity: 'error',
    });
    return { isValid: false, errors, warnings };
  }

  // Validate triggers
  if (!workflow.definition.triggers || Object.keys(workflow.definition.triggers).length === 0) {
    warnings.push({
      code: 'NO_TRIGGERS',
      message: 'Workflow has no triggers defined',
      path: 'definition.triggers',
      severity: 'warning',
    });
  }

  // Validate actions
  const actions = workflow.definition.actions ?? {};
  for (const [actionName, action] of Object.entries(actions)) {
    const actionErrors = validateAction(actionName, action, `definition.actions.${actionName}`);
    errors.push(...actionErrors.errors);
    warnings.push(...actionErrors.warnings);
  }

  // Validate runAfter references
  const runAfterErrors = validateRunAfterReferences(workflow);
  errors.push(...runAfterErrors);

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate a single action
 */
function validateAction(
  name: string,
  action: Action,
  path: string
): { errors: ValidationError[]; warnings: ValidationWarning[] } {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate action type
  if (!action.type) {
    errors.push({
      code: 'MISSING_TYPE',
      message: `Action "${name}" is missing a type`,
      path,
      severity: 'error',
    });
  }

  // Validate Agent actions
  if (isAgentAction(action)) {
    const agentValidation = validateAgentAction(name, action, path);
    errors.push(...agentValidation.errors);
    warnings.push(...agentValidation.warnings);
  }

  return { errors, warnings };
}

/**
 * Validate Agent-specific properties
 */
function validateAgentAction(
  name: string,
  action: AgentAction,
  path: string
): { errors: ValidationError[]; warnings: ValidationWarning[] } {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  const inputs = action.inputs as AgentInputs | undefined;

  // Check for system prompt
  if (!inputs?.systemPrompt) {
    warnings.push({
      code: 'AGENT_NO_SYSTEM_PROMPT',
      message: `Agent "${name}" has no system prompt configured`,
      path: `${path}.inputs.systemPrompt`,
      severity: 'warning',
    });
  }

  // Validate tools if present
  if (action.tools) {
    for (const [toolName, tool] of Object.entries(action.tools)) {
      if (!tool.description) {
        warnings.push({
          code: 'TOOL_NO_DESCRIPTION',
          message: `Tool "${toolName}" in Agent "${name}" has no description`,
          path: `${path}.tools.${toolName}.description`,
          severity: 'warning',
        });
      }
    }
  }

  return { errors, warnings };
}

/**
 * Validate runAfter references point to valid actions
 */
function validateRunAfterReferences(workflow: WorkflowDefinition): ValidationError[] {
  const errors: ValidationError[] = [];
  const allTriggers = Object.keys(workflow.definition.triggers ?? {});
  const allActions = Object.keys(workflow.definition.actions ?? {});
  const validTargets = new Set([...allTriggers, ...allActions]);

  for (const [actionName, action] of Object.entries(workflow.definition.actions ?? {})) {
    if (action.runAfter) {
      for (const dependency of Object.keys(action.runAfter)) {
        if (!validTargets.has(dependency)) {
          errors.push({
            code: 'INVALID_RUNAFTER_REFERENCE',
            message: `Action "${actionName}" has runAfter dependency "${dependency}" which does not exist`,
            path: `definition.actions.${actionName}.runAfter`,
            severity: 'error',
          });
        }
      }
    }
  }

  return errors;
}

// =============================================================================
// CONNECTION SERIALIZATION
// =============================================================================

/**
 * Serialize connections configuration
 */
export function serializeConnections(
  connections: ConnectionsConfiguration,
  options: SerializationOptions = {}
): string {
  const { pretty = true } = options;

  return pretty
    ? JSON.stringify(connections, null, 2)
    : JSON.stringify(connections);
}

/**
 * Merge connection changes into existing configuration
 */
export function mergeConnections(
  existing: ConnectionsConfiguration,
  updates: Partial<ConnectionsConfiguration>
): ConnectionsConfiguration {
  return {
    managedApiConnections: {
      ...(existing.managedApiConnections ?? {}),
      ...(updates.managedApiConnections ?? {}),
    },
    serviceProviderConnections: {
      ...(existing.serviceProviderConnections ?? {}),
      ...(updates.serviceProviderConnections ?? {}),
    },
    agentConnections: {
      ...(existing.agentConnections ?? {}),
      ...(updates.agentConnections ?? {}),
    },
    hostedAgentConnections: {
      ...(existing.hostedAgentConnections ?? {}),
      ...(updates.hostedAgentConnections ?? {}),
    },
  };
}

// =============================================================================
// TYPE GUARDS & HELPERS
// =============================================================================

/**
 * Check if action is an Agent action
 */
function isAgentAction(action: Action): action is AgentAction {
  return action.type === 'Agent';
}

/**
 * Check if action has nested actions
 */
function hasNestedActions(action: Action): boolean {
  const typedAction = action as { actions?: unknown; else?: { actions?: unknown }; cases?: unknown; default?: { actions?: unknown } };
  return !!(typedAction.actions || typedAction.else?.actions || typedAction.cases || typedAction.default?.actions);
}

/**
 * Check if value is an array of value segments
 */
function isValueSegmentArray(value: unknown[]): value is ValueSegment[] {
  if (value.length === 0) return false;
  const first = value[0];
  return (
    typeof first === 'object' &&
    first !== null &&
    'id' in first &&
    'type' in first &&
    'value' in first
  );
}

// =============================================================================
// EXPORT HELPERS
// =============================================================================

/**
 * Create a workflow export package with both workflow and connections
 */
export function createExportPackage(
  workflow: WorkflowDefinition,
  connections: ConnectionsConfiguration,
  options: SerializationOptions = {}
): { workflow: string; connections: string } {
  return {
    workflow: serializeWorkflow(workflow, options),
    connections: serializeConnections(connections, options),
  };
}

/**
 * Parse and validate a workflow JSON string
 */
export function parseWorkflow(json: string): {
  success: boolean;
  workflow?: WorkflowDefinition;
  error?: string;
} {
  try {
    const parsed = JSON.parse(json);

    // Basic structure validation
    if (!parsed.definition || typeof parsed.definition !== 'object') {
      return { success: false, error: 'Invalid workflow structure: missing definition' };
    }

    return { success: true, workflow: parsed as WorkflowDefinition };
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'Invalid JSON';
    telemetryService.trackEvent('workflow.serialization.error', {
      direction: 'deserialize',
      error: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
}

/**
 * Parse and validate a connections JSON string
 */
export function parseConnections(json: string): {
  success: boolean;
  connections?: ConnectionsConfiguration;
  error?: string;
} {
  try {
    const parsed = JSON.parse(json);

    if (typeof parsed !== 'object' || parsed === null) {
      return { success: false, error: 'Invalid connections structure: must be an object' };
    }

    return { success: true, connections: parsed as ConnectionsConfiguration };
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'Invalid JSON';
    telemetryService.trackEvent('canvas.serialization.error', {
      direction: 'deserialize',
      error: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
}

// =============================================================================
// ENCODING & CASTING UTILITIES
// =============================================================================

/**
 * Apply encoding to a string value based on options
 */
export function applyEncoding(value: string, options: EncodingOptions): string {
  let result = value;

  if (options.htmlEncode) {
    result = htmlEncode(result);
  }

  if (options.urlEncode) {
    result = encodeURIComponent(result);
  }

  if (options.base64Binary) {
    // Only encode if value looks like binary data
    if (isBinaryLike(result)) {
      result = btoa(result);
    }
  }

  return result;
}

/**
 * Apply type casting to a value based on options
 */
export function applyCasting(
  value: unknown,
  targetType: string,
  options: CastingOptions
): unknown {
  // Handle null values
  if (value === null || value === undefined) {
    return options.preserveNulls ? null : '';
  }

  // Cast string to number
  if (options.stringToNumber && typeof value === 'string' && targetType === 'number') {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return num;
    }
  }

  // Cast string to integer
  if (options.stringToNumber && typeof value === 'string' && targetType === 'integer') {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      return num;
    }
  }

  // Cast string to boolean
  if (options.stringToBoolean && typeof value === 'string' && targetType === 'boolean') {
    const lower = value.toLowerCase();
    if (lower === 'true' || lower === '1' || lower === 'yes') {
      return true;
    }
    if (lower === 'false' || lower === '0' || lower === 'no') {
      return false;
    }
  }

  // Cast array to CSV
  if (options.arrayToCsv && Array.isArray(value) && targetType === 'string') {
    return value.join(',');
  }

  return value;
}

/**
 * HTML encode a string
 */
function htmlEncode(str: string): string {
  const replacements: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return str.replace(/[&<>"']/g, (char) => replacements[char] ?? char);
}

/**
 * Check if a string looks like binary data
 */
function isBinaryLike(str: string): boolean {
  // Check for non-printable ASCII characters
  for (let i = 0; i < Math.min(str.length, 100); i++) {
    const code = str.charCodeAt(i);
    if (code < 32 && code !== 9 && code !== 10 && code !== 13) {
      return true;
    }
  }
  return false;
}

/**
 * Serialize a parameter value with encoding/casting options
 */
export function serializeParameterValueWithOptions(
  value: ParameterValue,
  targetType: string = 'string',
  options: SerializationOptions = {}
): unknown {
  // If it's an array of value segments, convert to string first
  let processed: unknown = value;
  if (Array.isArray(value) && isValueSegmentArray(value)) {
    processed = serializeSegmentsToValue(value);
  }

  // Apply casting if options provided
  if (options.casting) {
    processed = applyCasting(processed, targetType, options.casting);
  }

  // Apply encoding if options provided and value is a string
  if (options.encoding && typeof processed === 'string') {
    processed = applyEncoding(processed, options.encoding);
  }

  return processed;
}
